import { generateUuid } from "../../util/uuid";

export const MAX_REPORTS_PER_PAGE = 20;
export const MAX_STACK_CHARS = 4_000;
export const CLIENT_DIAGNOSTICS_PATH = "/api/ai/client-diagnostics";

const RETRY_DELAY_MS = 1_000;
/** The page's bundled scripts load from here in the app, whatever the page's own origin. */
const APP_BUNDLE_ORIGIN = "capacitor://localhost";

/** A stack frame in V8's form (`at fn (url:1:2)`, `at url:1:2`) and WebKit's (`fn@url:1:2`,
 *  `global code@url:1:2`). WebKit's function part is a name, never free text. */
const V8_FRAME = /^at (?:(.*?) \()?([^\s()]+):(\d+):(\d+)\)?$/;
const WEBKIT_FRAME =
    /^((?:async )?(?:[^\s@]*|global code|eval code|module code))@([^\s@]+):(\d+):(\d+)$/;

export interface WebErrorPayload {
    kind: "error" | "unhandledrejection";
    message: string;
    stack: string | null;
    source: string | null;
    line: number | null;
    column: number | null;
    path: string;
    occurred_at: string;
}

type Send = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** An error as caught: message, stack and source already cut down to what may leave the
 *  device (`describeError`, `bundleFrames`, `bundleUrl`), before redaction and truncation of
 *  the stack and source. */
type CaughtError = Omit<WebErrorPayload, "path" | "occurred_at">;

interface AppParts {
    send: Send;
    routePath: (pathname: string) => string;
    redactGuestTokens: (text: string) => string;
    currentPath: () => string;
}

/** What a report needs from the app, loaded with the first one: the app's authenticated
 *  transport (bearer, tenant headers, `X-Primepoint-Client`), the analytics route path and its
 *  guest-token redaction, and the route the memory router is on. The platform module that
 *  installs this hook stays free of that graph, several of whose modules read the installed
 *  platform as they load. */
async function loadAppParts(): Promise<AppParts> {
    const [{ authenticatedFetch }, { routePath, redactGuestTokens }, { currentNavigationPath }] =
        await Promise.all([
            import("../../util/authenticatedFetch"),
            import("../../util/routePath"),
            import("../../util/pageLoadTiming"),
        ]);
    return {
        send: authenticatedFetch,
        routePath,
        redactGuestTokens,
        currentPath: () => currentNavigationPath() ?? window.location.pathname,
    };
}

interface WebErrorReportingOptions {
    /** The page's window; a test passes its own. */
    target?: EventTarget;
    send?: Send;
    /** The route the member is on, before redaction. */
    currentPath?: () => string;
    now?: () => Date;
    retryDelayMs?: number;
}

function clip(text: string, limit: number): string {
    return text.length > limit ? text.slice(0, limit) : text;
}

/** The origins the page's own scripts load from: the app bundle and the page itself. */
function bundleOrigins(): string[] {
    const own = typeof window === "undefined" ? null : window.location.origin;
    return own && own !== "null" && own !== APP_BUNDLE_ORIGIN
        ? [APP_BUNDLE_ORIGIN, own]
        : [APP_BUNDLE_ORIGIN];
}

/** `url` without its query and fragment when it is one of the page's own scripts, else null. */
function bundleUrl(url: string): string | null {
    const bare = url.replace(/[?#].*$/, "");
    return bundleOrigins().some((origin) => bare.startsWith(`${origin}/`)) ? bare : null;
}

/** Only the frames in the page's own scripts, rebuilt from their parts with the script URL's
 *  query and fragment gone. Every other line is dropped: V8's first line repeats the message,
 *  and a frame in another script says nothing about this app's code. */
function bundleFrames(stack: string): string | null {
    const frames = stack.split("\n").flatMap((raw) => {
        const line = raw.trim();
        const v8 = V8_FRAME.exec(line);
        if (v8) {
            const url = bundleUrl(v8[2]);
            if (!url) return [];
            const at = `${url}:${v8[3]}:${v8[4]}`;
            return [v8[1] ? `at ${v8[1]} (${at})` : `at ${at}`];
        }
        const webkit = WEBKIT_FRAME.exec(line);
        const url = webkit && bundleUrl(webkit[2]);
        return webkit && url ? [`${webkit[1]}@${url}:${webkit[3]}:${webkit[4]}`] : [];
    });
    return frames.length > 0 ? frames.join("\n") : null;
}

function stackOf(value: unknown): string | null {
    return value instanceof Error && typeof value.stack === "string"
        ? bundleFrames(value.stack)
        : null;
}

/** What may be said about a thrown or rejected value: an error's name alone ("TypeError",
 *  "SyntaxError", "AbortError"), and for anything else only its type ("string rejection",
 *  "object rejection"). Never a message or a value's text: an engine error quotes data as
 *  readily as an app error (`new TypeError(text)`, the input `JSON.parse` choked on). */
function describeError(value: unknown, how: "thrown" | "rejection"): string {
    if (value instanceof Error) return clip(value.name || "Error", 100);
    if (value === undefined || value === null) {
        return how === "thrown" ? "error without an Error object" : `${String(value)} rejection`;
    }
    return `${Array.isArray(value) ? "array" : typeof value} ${how}`;
}

/** The first line of `stack` (a bundle frame, see `bundleFrames`), the server's grouping key
 *  with the message. */
function firstFrame(stack: string | null): string | null {
    return stack ? (stack.split("\n")[0] ?? null) : null;
}

function isRetryable(status: number): boolean {
    return status === 408 || status === 429 || status >= 500;
}

/**
 * The iOS shell's page reports its uncaught JavaScript errors and unhandled rejections to
 * ai_engine (`POST /api/ai/client-diagnostics`, `source: "web_error"`), beside the native
 * crash reports: inside the app nobody sees a console. Best effort: each distinct error
 * (message and first stack frame) once per page session, at most `MAX_REPORTS_PER_PAGE`, one
 * retry, then dropped with a console warning.
 *
 * No customer text leaves the device: the message is the error's name or the value's type
 * (`describeError`), the stack keeps only frames in the page's own scripts (`bundleFrames`), the
 * source only one of those scripts, and the route path is redacted as analytics redacts it
 * (`routePath`, `redactGuestTokens`). Installed on `target` (the page's window) by
 * `createIosPlatform`; returns the uninstall.
 */
export function installWebErrorReporting({
    target = window,
    send,
    currentPath,
    now = () => new Date(),
    retryDelayMs = RETRY_DELAY_MS,
}: WebErrorReportingOptions = {}): () => void {
    const reported = new Set<string>();
    let warnedCap = false;
    let appParts: Promise<AppParts> | null = null;
    const loadOnce = (): Promise<AppParts> => {
        appParts ??= loadAppParts().catch((error: unknown) => {
            appParts = null;
            throw error;
        });
        return appParts;
    };

    const deliver = async (caught: CaughtError, occurredAt: string): Promise<void> => {
        let failure: unknown = null;
        try {
            const app = await loadOnce();
            const redact = app.redactGuestTokens;
            const payload: WebErrorPayload = {
                ...caught,
                stack: caught.stack === null ? null : clip(redact(caught.stack), MAX_STACK_CHARS),
                source: caught.source === null ? null : redact(caught.source),
                path: app.routePath((currentPath ?? app.currentPath)()),
                occurred_at: occurredAt,
            };
            const body = JSON.stringify({
                report_id: generateUuid(),
                source: "web_error",
                payload,
            });
            for (let attempt = 0; attempt < 2; attempt += 1) {
                if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
                try {
                    const response = await (send ?? app.send)(CLIENT_DIAGNOSTICS_PATH, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body,
                    });
                    if (response.ok) return;
                    failure = `status ${response.status}`;
                    if (!isRetryable(response.status)) break;
                } catch (error) {
                    failure = error;
                }
            }
        } catch (error) {
            failure = error;
        }
        console.warn("[Diagnostics] Dropped a JavaScript error report:", failure);
    };

    const report = (caught: CaughtError) => {
        const signature = `${caught.message}\n${
            firstFrame(caught.stack) ?? `${caught.source}:${caught.line}:${caught.column}`
        }`;
        if (reported.has(signature)) return;
        if (reported.size >= MAX_REPORTS_PER_PAGE) {
            if (!warnedCap) {
                warnedCap = true;
                console.warn(
                    `[Diagnostics] ${MAX_REPORTS_PER_PAGE} JavaScript errors reported this session; no more are sent`,
                );
            }
            return;
        }
        reported.add(signature);
        void deliver(caught, now().toISOString());
    };

    // A fault in the hook itself must never raise another error event it would then report.
    const guarded =
        <E extends Event>(caughtFrom: (event: E) => CaughtError) =>
        (event: Event) => {
            try {
                report(caughtFrom(event as E));
            } catch (error) {
                console.warn("[Diagnostics] Could not report a JavaScript error:", error);
            }
        };

    const onError = guarded<ErrorEvent>((event) => {
        const source = event.filename ? bundleUrl(event.filename) : null;
        return {
            kind: "error",
            message: describeError(event.error, "thrown"),
            stack: stackOf(event.error),
            source,
            line: source ? event.lineno || null : null,
            column: source ? event.colno || null : null,
        };
    });
    const onRejection = guarded<PromiseRejectionEvent>((event) => ({
        kind: "unhandledrejection",
        message: describeError(event.reason, "rejection"),
        stack: stackOf(event.reason),
        source: null,
        line: null,
        column: null,
    }));

    target.addEventListener("error", onError);
    target.addEventListener("unhandledrejection", onRejection);
    return () => {
        target.removeEventListener("error", onError);
        target.removeEventListener("unhandledrejection", onRejection);
    };
}
